import { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { QueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { loginSchema, signupSchema, LoginInput, SignupInput } from "@/lib/validations";
import { setMonitoringUser } from "@/lib/monitoring";
import { processOwnerRollbackQueue } from "@/lib/patients/ownerRollbackQueue";
import { useTranslation } from "react-i18next";
import {
  CURRENT_CLINIC_CUSTOMER_ID_QUERY_KEY,
  CURRENT_CLINIC_ID_QUERY_KEY,
} from "@/lib/react-query/queryKeys";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (data: LoginInput) => Promise<{ error: string | null }>;
  signUp: (data: SignupInput) => Promise<{
    error: string | null;
    data?: Awaited<ReturnType<typeof supabase.auth.signUp>>["data"];
  }>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: string | null }>;
  updatePassword: (password: string) => Promise<{ error: string | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function getValidationErrorMessage(error: unknown, fallback: string): string {
  if (
    typeof error === "object" &&
    error !== null &&
    "errors" in error &&
    Array.isArray(error.errors) &&
    error.errors[0] &&
    typeof error.errors[0] === "object" &&
    error.errors[0] !== null &&
    "message" in error.errors[0] &&
    typeof error.errors[0].message === "string"
  ) {
    return error.errors[0].message;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
}

function getUnknownErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

export function AuthProvider({ children, queryClient }: { children: ReactNode; queryClient: QueryClient }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { t } = useTranslation("auth");
  const processedUserRef = useRef<string | null>(null);

  const invalidateSessionQueries = useCallback(() => {
    const queryKeys: Array<readonly unknown[]> = [
      ["profile"],
      ["current-user"],
      CURRENT_CLINIC_ID_QUERY_KEY,
      CURRENT_CLINIC_CUSTOMER_ID_QUERY_KEY,
      ["entitlements"],
      ["user-role"],
      ["user-permissions-map"],
      ["user-permissions-derived"],
      ["clinics-for-user"],
      ["permission-check"],
      ["permissions"],
      ["permissions-map"],
      ["is-owner"],
      ["is-super-admin"],
      ["is-veterinarian"],
      ["customers"],
    ];

    for (const queryKey of queryKeys) {
      queryClient.invalidateQueries({ queryKey, exact: false });
    }
  }, [queryClient]);

  const ensureCustomerAndClinic = useCallback(
    async (currentUser: User): Promise<boolean> => {
      const metadata = currentUser.user_metadata as Record<string, unknown> | null;
      const getMetadataString = (key: string): string | null => {
        const value = metadata?.[key];
        return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
      };

      const fullName =
        getMetadataString("full_name") ??
        getMetadataString("name") ??
        (currentUser.email ? currentUser.email.split("@")[0] ?? null : null) ??
        "Novo usuário";

      const clinicName =
        getMetadataString("clinic_name") ??
        getMetadataString("clinicName") ??
        (fullName ? `Clínica ${fullName}` : "Clínica");

      const phone = getMetadataString("phone");

      const { data: existingProfile, error: profileError } = await supabase
        .from("profiles")
        .select("id, current_clinic_id")
        .eq("id", currentUser.id)
        .limit(1);

      if (profileError) {
        console.error("Erro ao buscar perfil do usuário:", profileError);
        return false;
      }

      const profile = existingProfile?.[0] ?? null;
      if (profile?.current_clinic_id) return true;

      // A local database reset can leave an old browser JWT behind even though
      // its auth.users row no longer exists. It cannot be repaired client-side
      // because profiles.id has an FK to auth.users; clear the stale session.
      if (!profile) {
        console.warn("Sessão local obsoleta: o perfil não existe mais no banco.");
        await supabase.auth.signOut();
        return false;
      }

      const { error: bootstrapError } = await (supabase as any).rpc("bootstrap_my_organization", {
        p_full_name: fullName,
        p_clinic_name: clinicName,
        p_phone: phone,
      });

      if (bootstrapError) {
        console.error("Erro ao preparar organização inicial:", bootstrapError);
        return false;
      }

      // The bootstrap RPC atomically creates the profile, customer, owner access,
      // clinic, clinic access, and current clinic before this client continues.
      return true;

    },
    [],
  );

  useEffect(() => {
    // Set up auth state listener FIRST
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      if (event === 'SIGNED_OUT') {
        queryClient.clear();
      }
      // On SIGNED_IN, ensureCustomerAndClinic will run in the effect below and then invalidate
    });

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [queryClient]);

  // Ensure profile has customer/clinic on login or session restore so navigation and permissions load correctly
  useEffect(() => {
    if (!user) return;

    let cancelled = false;
    ensureCustomerAndClinic(user).then((success) => {
      if (!cancelled && success) {
        invalidateSessionQueries();
        void processOwnerRollbackQueue({ queryClient });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [user, ensureCustomerAndClinic, invalidateSessionQueries, queryClient]);

  // If we ever failed to rollback an owner after a patient-creation error, retry cleanup when the app comes back online.
  useEffect(() => {
    if (!user) return;

    const onOnline = () => {
      void processOwnerRollbackQueue({ queryClient });
    };

    window.addEventListener("online", onOnline);
    return () => {
      window.removeEventListener("online", onOnline);
    };
  }, [user, queryClient]);

  // Identify user in monitoring tools (LogRocket + Sentry) for support/debugging
  useEffect(() => {
    const metadata = user?.user_metadata as Record<string, unknown> | null;
    const getName = (): string | undefined => {
      const full = metadata?.full_name ?? metadata?.name;
      if (typeof full === "string" && full.trim()) return full.trim();
      if (user?.email) return user.email.split("@")[0];
      return undefined;
    };

    void setMonitoringUser(
      user
        ? {
          id: user.id,
          email: user.email ?? undefined,
          name: getName(),
        }
        : null,
    );
  }, [user]);

  const signIn = async (data: LoginInput) => {
    try {
      const validated = loginSchema.parse(data);

      const { error } = await supabase.auth.signInWithPassword({
        email: validated.email,
        password: validated.password,
      });

      if (error) {
        let errorMessage = t("errors.loginFailed");

        if (error.message.includes("Invalid login credentials")) {
          errorMessage = t("errors.invalidCredentials");
        } else if (error.message.includes("Email not confirmed")) {
          errorMessage = t("errors.emailNotConfirmed");
        }

        toast({
          variant: "destructive",
          title: t("errors.generic"),
          description: errorMessage,
        });

        return { error: errorMessage };
      }

      toast({
        title: t("errors.success"),
        description: t("errors.loginSuccess"),
      });

      return { error: null };
    } catch (error: unknown) {
      const errorMessage = getValidationErrorMessage(error, t("errors.invalidData"));
      toast({
        variant: "destructive",
        title: t("errors.generic"),
        description: errorMessage,
      });
      return { error: errorMessage };
    }
  };

  const signUp = async (data: SignupInput) => {
    try {
      const validated = signupSchema.parse(data);
      const redirectUrl = `${window.location.origin}/`;

      const { data: signUpData, error } = await supabase.auth.signUp({
        email: validated.email,
        password: validated.password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            full_name: validated.fullName,
            clinic_name: validated.clinicName,
            phone: validated.phone || null,
          },
        },
      });

      if (error) {
        let errorMessage = t("errors.signupFailed");

        if (error.message.includes("User already registered")) {
          errorMessage = t("errors.emailRegistered");
        } else if (error.message.includes("Password should be")) {
          errorMessage = t("errors.passwordRequirements");
        }

        toast({
          variant: "destructive",
          title: t("errors.generic"),
          description: errorMessage,
        });

        return { error: errorMessage };
      }

      console.log("signUpData", signUpData);

      if (signUpData?.session && signUpData.user) {
        const success = await ensureCustomerAndClinic(signUpData.user);
        processedUserRef.current = signUpData.user.id;
        if (!success) {
          toast({
            variant: "destructive",
            title: t("errors.setupAccountTitle"),
            description: t("errors.setupAccountDescription"),
          });
        }
      }

      toast({
        title: t("errors.signupSuccessTitle"),
        description: t("errors.signupSuccessDescription"),
      });

      return { error: null, data: signUpData };
    } catch (error: unknown) {
      const errorMessage = getValidationErrorMessage(error, t("errors.invalidData"));
      toast({
        variant: "destructive",
        title: t("errors.generic"),
        description: errorMessage,
      });
      return { error: errorMessage };
    }
  };

  const signInWithGoogle = async () => {
    try {
      const redirectUrl = `${window.location.origin}/calendar`;

      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: redirectUrl,
        },
      });

      if (error) {
        toast({
          variant: "destructive",
          title: t("errors.generic"),
          description: t("errors.googleLoginFailed"),
        });
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: t("errors.generic"),
        description: t("errors.googleLoginFailed"),
      });
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();

      if (error) {
        toast({
          variant: "destructive",
          title: t("errors.generic"),
          description: t("errors.logoutFailed"),
        });
      } else {
        toast({
          title: t("errors.logoutSuccessTitle"),
          description: t("errors.logoutSuccessDescription"),
        });
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: t("errors.generic"),
        description: t("errors.logoutFailed"),
      });
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const redirectUrl = `${window.location.origin}/reset-password`;
      console.log("redirectUrl", redirectUrl);
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });

      if (error) {
        toast({
          variant: "destructive",
          title: t("errors.generic"),
          description: t("errors.resetFailed"),
        });
        return { error: error.message };
      }

      toast({
        title: t("errors.resetSentTitle"),
        description: t("errors.resetSentDescription"),
      });

      return { error: null };
    } catch (error: unknown) {
      toast({
        variant: "destructive",
        title: t("errors.generic"),
        description: t("errors.resetFailed"),
      });
      return { error: getUnknownErrorMessage(error, t("errors.unknown")) };
    }
  };

  const updatePassword = async (password: string) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) {
        let errorMessage = t("errors.updatePasswordFailed");

        if (error.message.includes("Password should be")) {
          errorMessage = t("errors.passwordRequirements");
        }

        toast({
          variant: "destructive",
          title: t("errors.generic"),
          description: errorMessage,
        });
        return { error: errorMessage };
      }

      toast({
        title: t("errors.updatePasswordSuccessTitle"),
        description: t("errors.updatePasswordSuccessDescription"),
      });

      return { error: null };
    } catch (error: unknown) {
      toast({
        variant: "destructive",
        title: t("errors.generic"),
        description: t("errors.updatePasswordFailed"),
      });
      return { error: getUnknownErrorMessage(error, t("errors.unknown")) };
    }
  };

  return (
    <AuthContext.Provider
      value={{ user, session, loading, signIn, signUp, signInWithGoogle, signOut, resetPassword, updatePassword }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
